import React from "react";
import { ClockIcon } from "../icons";

interface TimeDisplayProps {
  timeFrom?: string;
  timeTo?: string;
  className?: string;
}

const TimeDisplay: React.FC<TimeDisplayProps> = ({
  timeFrom,
  timeTo,
  className = "",
}) => {
  const formatTime = (time?: string): string => {
    if (!time) return "";

    // If time is already in HH:mm format, return as is
    if (time.match(/^\d{2}:\d{2}$/)) {
      return time;
    }

    // If time has seconds (HH:mm:ss), remove seconds
    if (time.match(/^\d{2}:\d{2}:\d{2}$/)) {
      return time.substring(0, 5);
    }

    // If time is in H:mm format, add leading zero
    if (time.match(/^\d:\d{2}$/)) {
      return `0${time}`;
    }

    // If time is just H:m format, add leading zeros
    if (time.match(/^\d:\d$/)) {
      return `0${time.substring(0, 1)}:0${time.substring(2)}`;
    }

    // If time is just a number (e.g., "9"), treat as hour
    if (time.match(/^\d+$/)) {
      return `${time.padStart(2, "0")}:00`;
    }

    // Otherwise return as is
    return time;
  };

  if (!timeFrom && !timeTo) {
    return null;
  }

  // Case: only timeTo (no timeFrom)
  if (!timeFrom && timeTo) {
    return (
      <div className="flex flex-row gap-2 items-center">
        <ClockIcon className="w-4 h-4 text-slate-500" />
        <span className={`text-sm text-neutral-500 font-mono ${className}`}>
          do {formatTime(timeTo)}
        </span>
      </div>
    );
  }

  // Case: only timeFrom (no timeTo)
  if (timeFrom && !timeTo) {
    return (
      <div className="flex flex-row gap-2 items-center">
        <ClockIcon className="w-4 h-4 text-slate-500" />
        <span className={`text-sm text-neutral-500 font-mono ${className}`}>
          od {formatTime(timeFrom)}
        </span>
      </div>
    );
  }

  // Case: both timeFrom and timeTo
  return (
    <div className="flex flex-row gap-2 items-center">
      <ClockIcon className="w-4 h-4 text-slate-500" />
      <span className={`text-sm text-neutral-500 font-mono ${className}`}>
        {formatTime(timeFrom)} - {formatTime(timeTo)}
      </span>
    </div>
  );
};

export default TimeDisplay;
